import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { ClientService } from '../../../Services/client.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomToastrService } from '../../../Shared/Services/custom-toastr.service';
import { UserType } from '../../../Shared/Enums/UserType';
import { ClientType } from '../../../Shared/Enums/ClientType';
import { AuthService } from '../../../Shared/Services/auth.service';
import { DtoClientVM } from '../../../Model/clients/DtoClientVM';
import { DialogService } from '../../../Shared/Services/dialog.service';
import { environment } from '../../../../environments/environment'
import { SharedService } from '../../../Shared/Services/shared.service';


@Component({
  selector: 'app-create-client',
  templateUrl: './create-client.component.html',
  styleUrl: './create-client.component.scss'
})
export class CreateClientComponent {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  hidePassword = true;
  form!: FormGroup;
  fileUpload: any;//FileList
  ImgPath?: string;
  isEdit?: boolean = false;

  constructor(private api: ClientService, private spinner: NgxSpinnerService, private auth: AuthService,
    private router: Router, private toastrService: CustomToastrService, private route: ActivatedRoute,
    private dialogService: DialogService, private shared: SharedService) { }

  ngOnInit() {
    if (this.auth.isAuthenticated([UserType.Client, UserType.Workshop, UserType.Gallery, UserType.Agent])) {
      this.spinner.show();
      this.initForm();
      this.isEdit = false;
      this.route.params.subscribe(params => {
        const id = params['id'];
        if (id) {
          this.isEdit = true;
          this.GetClientData(id);
        }
      });

      this.spinner.hide();
    } else {
      this.router.navigateByUrl('dashboard/not-authorized');
    }
  }

  get f() {
    return this.form.controls;
  }

  initForm() {
    this.form = new FormGroup({
      id: new FormControl('', []),
      userName: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.email]),
      name: new FormControl('', [Validators.required]),
      clientAddress: new FormControl('', [Validators.required]),
      clientPhone: new FormControl('', [Validators.required, Validators.minLength(11), Validators.pattern(/^-?([0\u0660][0-9\u0660-\u0669]*)?$/)]),
      clientLogoURL: new FormControl('', [Validators.required])
    });
  }

  GetFileUpload(newItem: string) {
    this.fileUpload = newItem;
    const img = this.fileUpload[0];
    if (img != undefined) {
      this.form.setValue({
        clientLogoURL: img.name,
        id: this.form.value.id,
        userName: this.form.value.userName,
        password: this.form.value.password,
        email: this.form.value.email,
        name: this.form.value.name,
        clientAddress: this.form.value.clientAddress,
        clientPhone: this.form.value.clientPhone
      });
    }
  }

  submit() {
    if (this.form.valid) {
      this.dialogService.confirmDialog({
        titleKey: 'confirmation',
        messageKey: 'AreYouSuretoSaveThis',
        confirmTextKey: 'Yes',
        cancelTextKey: 'No'
      }).subscribe(isConfirmed => {
        if (isConfirmed) {
          this.SubmitClient(this.form.value);
        }
      });
    }
    else {
      this.shared.FireFormValidation(this.form);
    }
  }

  SubmitClient(form: any) {
    this.spinner.show();

    const formData = new FormData();
    if (this.fileUpload) {
      const img = this.fileUpload[0];
      if (img != undefined) {
        formData.append('photo', img);
      }
    }

    formData.append('name', form.name);
    formData.append('clientAddress', form.clientAddress);
    formData.append('clientPhone', form.clientPhone);
    formData.append('email', form.email);
    formData.append('userName', form.userName);
    formData.append('password', form.password);
    formData.append('clientTypeId', ClientType.customer.toString());
    formData.append('userType', UserType.Client.toString());

    if (form.id) {
      formData.append('id', form.id);
      this.EditClient(formData);
    }
    else {
      this.AddClient(formData)
    }
  }

  AddClient(formData: FormData) {
    this.api.CreateNewClient(formData).pipe(takeUntil(this.destroy$))
      .subscribe(
        {
          next: (data: any) => {
            this.spinner.hide();
            if (data != null) {
              if (data.isSuccess) {
                this.toastrService.showSuccessToast('Success', 'AddedSuccessfully');
                setTimeout(() => { this.router.navigateByUrl('dashboard/clients'); }, 600);
              } else {
                this.toastrService.showErrorToast('Error', data.message);
              }
            }
            else {
              this.toastrService.showErrorToast('Error', "SomethingWentWrong");
            }
          },
          error: (error: any) => {
            this.spinner.hide();
            this.toastrService.showErrorToast('Error', "SomethingWentWrong");
          }
        });
  }

  EditClient(formData: FormData) {
    this.api.EditClient(formData).pipe(takeUntil(this.destroy$))
      .subscribe(
        {
          next: (data: any) => {
            this.spinner.hide();
            if (data.isSuccess) {
              this.toastrService.showSuccessToast('Success', 'EditedSuccessfully');
              setTimeout(() => { this.router.navigateByUrl('dashboard/clients'); }, 600);
            } else {
              this.toastrService.showErrorToast('Error', data.message);
            }
          },
          error: (error: any) => {
            this.spinner.hide();
            this.toastrService.showErrorToast('Error', "SomethingWentWrong");
          }
        });
  }

  GetClientData(clientId: any) {
    if (this.auth.isAuthenticated([UserType.Client, UserType.Workshop, UserType.Gallery])) {
      this.api.GetClientData(clientId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data: any) => {
            this.spinner.hide();
            if (data.isCompleted) {
              this.setFormValue(data.result.payload);
            }
          },
          error: (error: any) => {
            this.spinner.hide();
            this.toastrService.showErrorToast('Error', "SomethingWentWrong");
          }
        });
    }
    else {
      this.router.navigateByUrl('dashboard/not-authorized');
    }
  }

  setFormValue(data: DtoClientVM) {
    this.form.setValue({
      id: data.id,
      userName: data.userName,
      password: '',
      email: data.email,
      name: data.name,
      clientAddress: data.clientAddress,
      clientPhone: data.clientPhone,
      clientLogoURL: data.clientLogoURL
    });

    if (data.clientLogoURL) {
      this.form.controls["clientLogoURL"].setValue(data.clientLogoURL);
      this.ImgPath = environment.ImgUrl + "/" + data.clientLogoURL;
    }
  }

  Cancel() {
    if (this.form.touched) {
      this.dialogService.confirmDialog({
        titleKey: 'confirmation',
        messageKey: 'AreYouSuretoBackWithoutSave',
        confirmTextKey: 'Yes',
        cancelTextKey: 'No'
      }).subscribe(isConfirmed => {
        if (isConfirmed) {
          this.router.navigateByUrl('dashboard/clients');
        }
      });
    }
    else {
      this.router.navigateByUrl('dashboard/clients');
    }
  }
}
